/**
 * Public storefront hero (TNK-105 phase 2). The store name, a one-line match
 * summary, and the game-pill row that doubles as a quick game filter — rendered
 * as the overlay content inside the shared, operator-configurable `HeroBand`
 * (TNK-204), so the banner matches the /shop landing.
 */

import { HeroBand } from "./HeroBand";

interface ShopHeroProps {
  storeName: string;
  heroRef: string | null;
  heroHeight: number | null;
  heroOpacity: number | null;
  heroFit: string | null;
  filteredCount: number;
  totalCount: number;
  priceMin: number;
  priceMax: number;
  games: string[];
  activeGame: string[];
  onGameSelect: (game: string[]) => void;
}

export function ShopHero({
  storeName,
  heroRef,
  heroHeight,
  heroOpacity,
  heroFit,
  filteredCount,
  totalCount,
  games,
  activeGame,
  onGameSelect,
}: ShopHeroProps) {
  return (
    <HeroBand
      heroRef={heroRef}
      heroHeight={heroHeight}
      heroOpacity={heroOpacity}
      heroFit={heroFit}
    >
      <h1
        className="shop-display font-black"
        style={{
          fontSize: "clamp(20px, 3.5vw, 32px)",
          color: "var(--color-foreground)",
        }}
      >
        {storeName}
      </h1>
      {/* TNK-116: only the filtered-match count is shown; catalog totals + the
          price spread are competitive intel kept off the public band. */}
      {filteredCount < totalCount && (
        <p
          className="mt-1 text-xs"
          style={{ color: "var(--color-muted-foreground)" }}
        >
          {filteredCount} matching
        </p>
      )}

      {games.length > 1 && (
        <div className="mt-4 flex w-fit max-w-full flex-wrap gap-2 rounded-xl border border-white/10 bg-black/30 p-2.5 shadow-xl backdrop-blur-sm">
          <GamePill
            label="All"
            active={activeGame.length === 0}
            onClick={() => onGameSelect([])}
          />
          {games.map((game) => (
            <GamePill
              key={game}
              label={game}
              active={activeGame.length === 1 && activeGame[0] === game}
              onClick={() =>
                onGameSelect(
                  activeGame.length === 1 && activeGame[0] === game
                    ? []
                    : [game],
                )
              }
            />
          ))}
        </div>
      )}
    </HeroBand>
  );
}

function GamePill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded px-3 py-1 text-xs transition-colors"
      style={{
        background: active ? "rgba(201,168,76,0.2)" : "rgba(255,255,255,0.04)",
        border: active
          ? "1px solid rgba(201,168,76,0.5)"
          : "1px solid rgba(255,255,255,0.08)",
        color: active
          ? "var(--color-primary)"
          : "var(--color-muted-foreground)",
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}
